import { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { SearchBox } from '@/components/search-box/search-box';
import { cn } from '@/lib/utils';

const NAV_LINKS = [
  { href: '/', label: 'Inicio' },
  { href: '/quienes-somos', label: 'Quiénes somos' },
  { href: '/estadisticas', label: 'Estadísticas' },
  { href: '/faq', label: 'Preguntas Frecuentes' },
];

export function Header(): React.ReactElement {
  const [isScrolled, setIsScrolled] = useState(() => window.scrollY > 20);
  const [scrollProgress, setScrollProgress] = useState(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    return docHeight > 0 ? Math.min(100, Math.max(0, (scrollTop / docHeight) * 100)) : 0;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);

    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    setScrollProgress(Math.min(100, Math.max(0, progress)));
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  function closeMobileMenu(): void {
    setIsMobileMenuOpen(false);
  }

  return (
    <>
      <header
        className={cn(
          'fixed top-0 right-0 left-0 z-99999 overflow-visible border border-white/30 shadow-2xl backdrop-blur-3xl transition-all duration-700 ease-out',
          isScrolled ? 'rounded-b-[30px] border-white/5 bg-white/10' : 'border-white/30 bg-white/20'
        )}
        style={{
          boxShadow: isScrolled
            ? '0 35px 60px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            : undefined,
        }}
      >
        <div className="absolute inset-0 bg-white/15 backdrop-blur-3xl transition-all duration-700" />
        <div className="absolute inset-0 bg-linear-to-b from-white/30 via-white/15 to-white/5 transition-all duration-700" />
        <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-r via-transparent transition-all duration-700" />
        <div className="from-card/20 to-primary/5 absolute inset-0 bg-linear-to-br via-transparent transition-all duration-700" />
        <div className="from-secondary/5 to-card/15 absolute inset-0 bg-linear-to-tl via-transparent transition-all duration-700" />

        <div className="absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent via-white/90 to-transparent transition-all duration-700" />

        <div className="absolute inset-0 opacity-50">
          <div className="absolute top-1/4 left-1/4 h-3 w-3 animate-pulse rounded-full bg-white/50" />
          <div className="absolute top-3/4 right-1/3 h-2 w-2 animate-pulse rounded-full bg-white/40 delay-1000" />
          <div className="absolute top-1/2 left-1/2 h-2.5 w-2.5 animate-pulse rounded-full bg-white/30 delay-500" />
        </div>

        <div className="relative z-10">
          <div className="container mx-auto px-6 lg:px-12 xl:px-16 2xl:px-24">
            <div className="flex h-14 items-center justify-between">
              <Link to="/" className="group flex items-center space-x-2">
                <div className="relative">
                  <img
                    src="/apeps-isotipo.png"
                    alt="APEPS Isotipo"
                    className="group-hover:drop-shadow-3xl h-8 w-auto transform object-contain drop-shadow-2xl transition-all duration-700 ease-out group-hover:-translate-y-1 group-hover:scale-110 group-hover:rotate-2 group-hover:brightness-110 group-hover:filter"
                    style={{ filter: 'brightness(1.1) contrast(1.1)' }}
                  />
                  <div className="from-primary to-secondary absolute inset-0 rounded-lg bg-linear-to-r opacity-0 transition-all duration-700 ease-out group-hover:scale-110 group-hover:rotate-2 group-hover:opacity-30" />
                </div>
                <div className="text-foreground group-hover:text-primary text-lg font-bold tracking-wide transition-all duration-500 ease-out">
                  APEPS
                </div>
              </Link>

              <nav className="hidden h-full flex-1 items-center justify-center lg:flex">
                <div className="flex h-full items-center space-x-5">
                  {NAV_LINKS.map(link => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="group text-foreground hover:border-border/50 hover:bg-card/40 hover:text-primary hover:shadow-medium relative transform rounded-xl border border-transparent px-2.5 py-1 text-sm font-medium transition-all duration-500 ease-out hover:-translate-y-1 hover:scale-105 hover:rotate-1 hover:backdrop-blur-xl"
                    >
                      {link.label}
                      <div className="from-primary to-secondary absolute bottom-0 left-1/2 h-0.5 w-0 -translate-x-1/2 transform bg-linear-to-r transition-all duration-700 ease-out group-hover:w-full group-hover:scale-x-110" />
                    </Link>
                  ))}
                  <Link
                    to="/ensuass"
                    className="group from-secondary to-secondary/80 text-secondary-foreground hover:border-secondary/40 hover:from-secondary/90 hover:to-secondary hover:shadow-medium relative transform rounded-xl border border-transparent bg-linear-to-r px-2.5 py-1 text-sm font-medium transition-all duration-500 ease-out hover:-translate-y-1 hover:scale-105"
                  >
                    Datos ENSUASS
                  </Link>
                </div>
              </nav>

              <div className="relative z-50 hidden items-center xl:flex">
                <SearchBox />
              </div>

              <div className="relative z-100 ml-6 hidden items-center space-x-3 lg:flex">
                <Link to="/login" title="Iniciar Sesión">
                  <button className="from-primary to-secondary text-primary-foreground shadow-medium hover:shadow-strong transform rounded-full bg-linear-to-r p-2 transition-all duration-300 ease-out hover:scale-110">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </button>
                </Link>
              </div>

              <div className="relative z-100 flex items-center space-x-2 lg:hidden">
                <Link to="/login" title="Iniciar Sesión">
                  <button className="from-primary to-secondary text-primary-foreground shadow-medium hover:shadow-strong transform rounded-full bg-linear-to-r p-2 transition-all duration-300 ease-out hover:scale-110">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </button>
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="rounded-full border border-white/50 bg-white/30 p-2 shadow-lg backdrop-blur-xl transition-all duration-300 ease-out hover:scale-110 hover:bg-white/50"
                  aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú de navegación'}
                  aria-expanded={isMobileMenuOpen}
                >
                  <svg
                    className="text-foreground h-5 w-5 transition-all duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          role="progressbar"
          aria-valuenow={Math.round(scrollProgress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progreso de lectura"
          className="from-primary via-primary to-secondary absolute bottom-0 left-0 h-1 origin-left bg-linear-to-r opacity-90 transition-[width] duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </header>

      <div
        role="button"
        tabIndex={-1}
        aria-label="Cerrar menú"
        className={cn(
          'fixed inset-0 z-99998 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-out lg:hidden',
          isMobileMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={closeMobileMenu}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') closeMobileMenu();
        }}
      />

      <div
        className={cn(
          'fixed top-0 left-0 z-99999 h-screen w-80 transform border-r border-white/30 bg-white/90 shadow-2xl backdrop-blur-3xl transition-transform duration-300 ease-out lg:hidden',
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="absolute inset-0 bg-white/15 backdrop-blur-3xl" />
        <div className="absolute inset-0 bg-linear-to-b from-white/30 via-white/15 to-white/5" />
        <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-r via-transparent" />

        <div className="absolute top-0 right-0 left-0 h-px bg-linear-to-r from-transparent via-white/90 to-transparent" />

        <div className="relative z-10 flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-white/20 px-6 py-4">
            <div className="flex items-center space-x-2">
              <img
                src="/apeps-isotipo.png"
                alt="APEPS"
                className="h-8 w-auto object-contain drop-shadow-xl"
                style={{ filter: 'brightness(1.1) contrast(1.1)' }}
              />
              <span className="text-foreground text-lg font-bold">APEPS</span>
            </div>
            <button
              onClick={closeMobileMenu}
              className="border-border/50 bg-card/30 shadow-medium hover:bg-card/50 rounded-full border p-2 backdrop-blur-xl transition-all duration-300 ease-out hover:scale-110"
              aria-label="Cerrar menú"
            >
              <svg
                className="text-foreground h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-6">
            <div className="flex flex-col space-y-2">
              {NAV_LINKS.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={closeMobileMenu}
                  className="group text-foreground hover:border-border/50 hover:bg-card/40 hover:text-primary hover:shadow-medium relative transform rounded-xl border border-transparent px-6 py-4 font-medium transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-105 hover:backdrop-blur-xl"
                >
                  <div className="flex items-center space-x-3">
                    <span>{link.label}</span>
                  </div>
                </Link>
              ))}
              <Link
                to="/ensuass"
                onClick={closeMobileMenu}
                className="group from-secondary to-secondary/80 text-secondary-foreground hover:border-secondary/40 hover:from-secondary/90 hover:to-secondary hover:shadow-medium relative transform rounded-xl border border-transparent bg-linear-to-r px-6 py-4 font-medium transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-105"
              >
                <div className="flex items-center space-x-3">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  <span>Datos ENSUASS</span>
                </div>
              </Link>
            </div>
          </nav>

          <div className="border-border/20 border-t px-6 py-4">
            <p className="text-muted-foreground text-center text-xs">© 2025 APEPS</p>
          </div>
        </div>
      </div>
    </>
  );
}
